/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { jsx } from '@atlaskit/css';
import FeedbackCollector from '@atlaskit/feedback-collector';
import { useProfileController } from '@atlassian/conversation-assistant-store';

import { CategoryPillGroup } from './category-pill-group';
import {
	EmojiRatingSelector,
	type ExperienceRating,
	ExperienceRatingToNumber,
} from './emoji-rating-selector';
import { messages } from './messages';
import { type SmartCreateFeedbackProps, SmartCreateFeedbackVariant } from './types';

const ANALYTICS_CHANNEL = 'aiMate';

const ENTRYPOINT_ID = '63867aef-06e5-4520-855f-4d29e062f22c';

const FEEDBACK_CONTEXT_FIELD_ID = 'customfield_10047';

const constructFeedbackContext = (additionalFields: object, aaid: string | undefined) => {
	const userAgent = navigator.userAgent;
	const screenResolution = `${window.screen.width} x ${window.screen.height}`;

	const feedbackContext = {
		'User-Agent': userAgent,
		'Screen Resolution': screenResolution,
		'User Account Id': aaid ?? 'unknown',
		/* Other context fields will have to be passed in as props, since there aren't public hooks to get these.
		Environment: metadata.environment ?? '',
		'Cloud Id': metadata.cloudId ?? '',
		Product: metadata.product ?? '',
		Edition: metadata.edition ?? '',
		*/
		...additionalFields,
	};

	const feedbackContextStr = Object.entries(feedbackContext)
		.map(([key, value]) => `${key}: ${value}`)
		.join('\n');

	return [
		{
			id: FEEDBACK_CONTEXT_FIELD_ID,
			value: feedbackContextStr,
		},
	];
};

const getFeedbackConfig = (variant: SmartCreateFeedbackVariant) => {
	switch (variant) {
		case SmartCreateFeedbackVariant.CREATE_WITH_ROVO:
			return {
				feedbackTitle: messages.feedbackTitle,
				rateExperience: messages.rateExperience,
			};
		case SmartCreateFeedbackVariant.ROVO:
			return {
				feedbackTitle: messages.feedbackTitleNoCreate,
				rateExperience: messages.rateExperienceNoCreate,
			};
	}
};

export function SmartCreateFeedback(props: SmartCreateFeedbackProps) {
	const { name, aaid, email } = useProfileController();

	return <SmartCreateFeedbackInternal name={name} aaid={aaid} email={email} {...props} />;
}

type InternalProps = {
	name: string;
	aaid: string | undefined;
	email: string | undefined;
};
export function SmartCreateFeedbackInternal({
	variant = SmartCreateFeedbackVariant.CREATE_WITH_ROVO,
	onClose,
	onSubmit,
	additionalFields = {},
	name,
	aaid,
	email,
}: SmartCreateFeedbackProps & InternalProps) {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { feedbackTitle, rateExperience } = getFeedbackConfig(variant);

	const categories = useMemo(
		() => [
			{ id: 'performance', text: formatMessage(messages.categoryPerformance) },
			{ id: 'response-quality', text: formatMessage(messages.categoryResponseQuality) },
			{ id: 'user-experience', text: formatMessage(messages.categoryUserExperience) },
			{ id: 'other', text: formatMessage(messages.categoryOther) },
		],
		[formatMessage],
	);

	const [experienceRating, setExperienceRating] = useState<ExperienceRating | undefined>(undefined);
	const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'smartCreateFeedbackCollector',
			attributes: {
				feedbackVariant: variant,
			},
			tags: ['atlaskit'],
		}).fire(ANALYTICS_CHANNEL);
	}, [variant, createAnalyticsEvent]);

	const onCloseWithAnalytics = useCallback(() => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'closed',
			actionSubject: 'smartCreateFeedbackCollector',
			attributes: {
				feedbackVariant: variant,
			},
			tags: ['atlaskit'],
		}).fire(ANALYTICS_CHANNEL);
		onClose();
	}, [onClose, variant, createAnalyticsEvent]);
	const onSubmitWithAnalytics = useCallback(() => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'submitted',
			actionSubject: 'smartCreateFeedbackCollector',
			attributes: {
				feedbackVariant: variant,
				experienceRating,
				selectedCategory,
			},
			tags: ['atlaskit'],
		}).fire(ANALYTICS_CHANNEL);
		onSubmit();
	}, [onSubmit, variant, experienceRating, selectedCategory, createAnalyticsEvent]);

	const customFields = {
		'Feedback Variant': variant,
		'Selected Category': selectedCategory ?? '',
		'Experience Rating': experienceRating ?? '',
		'Experience Rating Number':
			experienceRating !== undefined ? ExperienceRatingToNumber[experienceRating] : '',
		...additionalFields,
	};

	return (
		<FeedbackCollector
			onClose={onCloseWithAnalytics}
			onSubmit={onSubmitWithAnalytics}
			atlassianAccountId={aaid}
			name={name}
			email={email}
			entrypointId={ENTRYPOINT_ID}
			feedbackTitle={formatMessage(feedbackTitle)}
			showTypeField={false}
			customTextAreaLabel={formatMessage(messages.summaryTitle)}
			customContent={
				<>
					<EmojiRatingSelector
						rating={experienceRating}
						setRating={setExperienceRating}
						label={formatMessage(rateExperience)}
					/>
					<CategoryPillGroup
						categories={categories}
						selectedCategory={selectedCategory}
						setSelectedCategory={setSelectedCategory}
						label={formatMessage(messages.categoryTitle)}
					/>
				</>
			}
			disableSubmitButton={experienceRating === undefined}
			additionalFields={constructFeedbackContext(customFields, aaid)}
		/>
	);
}
